import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CertIcon from '@mui/icons-material/VerifiedUser';
import { WorkerTraining, Worker } from '../../types/worker';
import { fetchTraining, addTraining, fetchWorkers } from '../../services/workerService';

interface TrainingTabProps {
  farmId: string;
}

const TrainingTab: React.FC<TrainingTabProps> = ({ farmId }) => {
  const [training, setTraining] = useState<WorkerTraining[]>([]);
  const [workers, setWorkers] = useState<Worker[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newTraining, setNewTraining] = useState<Partial<WorkerTraining>>({
    farmId,
    certificationName: '',
    provider: '',
    status: 'active'
  });

  useEffect(() => {
    loadData();
  }, [farmId]);

  const loadData = async () => {
    try {
      const [trainingData, workersData] = await Promise.all([
        fetchTraining(farmId),
        fetchWorkers(farmId)
      ]);
      setTraining(trainingData);
      setWorkers(workersData);
    } catch (error) {
      console.error('Error loading training data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTraining = async () => {
    try {
      if (!newTraining.workerId || !newTraining.certificationName) return;
      
      await addTraining({ ...newTraining, farmId });
      setOpen(false);
      loadData();
      setNewTraining({ farmId, certificationName: '', provider: '', status: 'active' });
    } catch (error) {
      console.error('Error adding training:', error);
    }
  };

  const getWorkerName = (id: string) => {
    return workers.find(w => w.id === id)?.name || 'Unknown Worker';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'expired': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h6">Training & Certifications</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          Add Certification
        </Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Worker</TableCell>
              <TableCell>Certification</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell>Completion Date</TableCell>
              <TableCell>Expiry Date</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {training.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">No training records found</TableCell>
              </TableRow>
            ) : (
              training.map((record) => (
                <TableRow key={record.id}>
                  <TableCell sx={{ fontWeight: 500 }}>{getWorkerName(record.workerId)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CertIcon fontSize="small" color="primary" />
                      {record.certificationName}
                    </Box>
                  </TableCell>
                  <TableCell>{record.provider || '-'}</TableCell>
                  <TableCell>{record.completionDate || '-'}</TableCell>
                  <TableCell>{record.expiryDate || '-'}</TableCell>
                  <TableCell>
                    <Chip 
                      label={record.status.toUpperCase()} 
                      color={getStatusColor(record.status) as any} 
                      size="small" 
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Certification</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                label="Worker"
                fullWidth
                value={newTraining.workerId || ''}
                onChange={(e) => setNewTraining({ ...newTraining, workerId: e.target.value })}
              >
                {workers.map((worker) => (
                  <MenuItem key={worker.id} value={worker.id}>
                    {worker.name} ({worker.role})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Certification Name"
                fullWidth
                value={newTraining.certificationName || ''}
                onChange={(e) => setNewTraining({ ...newTraining, certificationName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Provider / Institute"
                fullWidth
                value={newTraining.provider || ''}
                onChange={(e) => setNewTraining({ ...newTraining, provider: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Completion Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newTraining.completionDate || ''}
                onChange={(e) => setNewTraining({ ...newTraining, completionDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Expiry Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newTraining.expiryDate || ''}
                onChange={(e) => setNewTraining({ ...newTraining, expiryDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                label="Status"
                fullWidth
                value={newTraining.status || 'active'}
                onChange={(e) => setNewTraining({ ...newTraining, status: e.target.value as any })}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddTraining}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingTab;
